import { Component } from 'react';
import { translate } from 'react-i18next';
import moment from 'moment';

// Components
import CertificateIcon from '../../../components/CertificateIcon';
import NoteBlock from './NoteBlock';
import TimeBlock from './TimeBlock';
import PatientBlock from './PatientBlock';

// Constants & Services
import { getAppointmentViewState } from 'src/services/functions';
import { PATIENT_NOT_VALIDATED, TAB_INVOICE, ROLES } from 'services/constants';
import { convertStatusToRowClass } from '../../services/appointmentUI';

// Styles & Images
import { CellText, StatusButton, TableRow } from './tableStyles';

class TableRowComponent extends Component {
  constructor() {
    super();
    this.state = {
      updatedStatus: '',
      isDisableBtn: false
    };
    this.goToAppointmentDetails = this.goToAppointmentDetails.bind(this);
    this.viewStatus = this.viewStatus.bind(this);
    this.triggerAppointmentStatus = this.triggerAppointmentStatus.bind(this);
    this.renderCertificate = this.renderCertificate.bind(this);
    this.renderStatusItems = this.renderStatusItems.bind(this);
    this.stopEvent = this.stopEvent.bind(this);
    this.timerId;
  }

  componentDidUpdate(prevProps) {
    const { appointment, selectedAppointmentStatus, updateSingleAppointmentInList } = this.props;
    const appointViewState = getAppointmentViewState(appointment);
    const prevAppointViewState = getAppointmentViewState(prevProps.appointment);
    if (appointment && prevProps.appointment) {
      if (
        (this.state.isDisableBtn && appointViewState !== prevAppointViewState) ||
        (prevProps.appointment &&
          appointment.errorTimeStamp !== prevProps.appointment.errorTimeStamp)
      ) {
        this.setState({ isDisableBtn: false });
      }
      if (prevAppointViewState !== appointViewState && appointment.updateFromSockets) {
        this.setState({ updatedStatus: 'updated-status' });
        this.timerId = setTimeout(() => {
          if (Array.isArray(selectedAppointmentStatus) &&
            selectedAppointmentStatus.find(statuses => statuses.indexOf(appointViewState) > -1)) {
            this.setState({ updatedStatus: '' });
          } else {
            this.setState({ updatedStatus: 'hide' });
            updateSingleAppointmentInList(appointment.id, { updateFromSockets: false });
          }
        }, 4000);
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timerId);
  }

  stopEvent(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
  }

  goToAppointmentDetails(e, tab) {
    this.stopEvent(e);
    if (e.target.id === 'status') return false;
    let { appointment, push, addActiveAppointment, setBackUrl, changeTab } = this.props;
    addActiveAppointment(appointment);
    setBackUrl();
    if (tab) changeTab(tab);
    push(`/practitioner/appointments/${appointment.id}/appointment`);
  }

  triggerAppointmentStatus(e, triggerIndex) {
    const {
      appointment: {
        appointment: {
          entity: { externalAppointmentId },
          triggerInfo: { triggers }
        }
      },
      triggerWorkflow
    } = this.props;
    this.stopEvent(e);
    this.setState({ isDisableBtn: true });
    triggerWorkflow(triggers[triggerIndex || 0], externalAppointmentId);
  }

  renderStatusItems(options, onClick, disabled) {
    const { appointment, i18n } = this.props;
    const { isDisableBtn } = this.state;
    const triggers = appointment.appointment.triggerInfo.triggers;
    const appointViewState = getAppointmentViewState(appointment);
    let localIsDisabledBtn = isDisableBtn || !moment(appointment.dateTime).isSame(moment(), 'day');

    const debugItem = true ? null : <div
      style={{ position: 'absolute', height: '100%', width: '100%', right: 0, bottom: 0 }}>
      <p style={{ position: 'absolute', top: 0, right: 0, margin: 0, color: 'red' }}>
        {appointViewState}
      </p>
      <p style={{ position: 'absolute', bottom: 0, right: 0, margin: 0 }}>
        {triggers.map(t => <span>{t} </span>)}
      </p></div>;

    if (options.type === 'button') {
      return <StatusButton
        disabled={disabled || localIsDisabledBtn}
        onClick={onClick}
      >
        {i18n.t(options.translateKey)}
        {debugItem}
      </StatusButton>;
    }
    return <CellText>{i18n.t(options.translateKey)} {debugItem}</CellText>;
  }

  viewStatus() {
    const { appointment, push, setBackUrl, openVerifyModal, role } = this.props;
    const { isDisableBtn } = this.state;
    const appointViewState = getAppointmentViewState(appointment);
    let localIsDisabledBtn = isDisableBtn || !moment(appointment.dateTime).isSame(moment(), 'day');

    const redirectToInvoice = e => {
      this.stopEvent(e);
      setBackUrl();
      if (appointment.invoice && appointment.invoice.id) {
        push(
          `/practitioner/appointments/${appointment.id}/create-invoice/${appointment.invoice.id}`
        );
      } else {
        push(`/practitioner/appointments/${appointment.id}/create-invoice`);
      }
    };

    switch (appointViewState) {
      case 'consultation_booked': {
        // appointment status - consultation_booked
        // trigger            - patient_arrive
        return this.renderStatusItems(
          { type: 'button', translateKey: 'register_arrival', },
          (e => {
            if (PATIENT_NOT_VALIDATED === appointment.patient.status) {
              this.stopEvent(e);
              openVerifyModal(appointment.patient.externalId);
            }
            this.triggerAppointmentStatus(e);
          }));
      }
      case 'patient_arrived': {
        // appointment status - patient_arrive
        // trigger            - consultation_start radiology_start
        if (role === ROLES.RADIOLOGY) {
          return this.renderStatusItems(
            { type: 'button', translateKey: 'radiology_start' },
            e => this.triggerAppointmentStatus(e, 1)); // radiology_start
        }
        return this.renderStatusItems(
          { type: 'button', translateKey: 'start_consultation_button' },
          this.triggerAppointmentStatus); // consultation_start
      }
      case 'consultation_started': {
        // appointment status - consultation_started
        // trigger            - consultation_startedconsultation_finish &
        //                      radiology_start_during_consultation
        if (role === ROLES.RADIOLOGY) {
          return this.renderStatusItems(
            { type: 'button', translateKey: 'radiology_start' },
            e => this.triggerAppointmentStatus(e, 1));
        }
        return this.renderStatusItems(
          { type: 'button', translateKey: 'create_invoice' },
          redirectToInvoice);
      }
      case 'radiology_started':
      case 'radiology_started_during_consultation': {
        // appointment status - radiology_started || radiology_started_during_consultation
        // trigger            - radiology_finish
        if (role === ROLES.RADIOLOGY) {
          // translate Finish radiology
          return this.renderStatusItems({ type: 'button', translateKey: 'radiology_finish' },
            this.triggerAppointmentStatus);
        }
        // translate From radiology
        return this.renderStatusItems({ type: 'button', translateKey: 'from_radiology' },
          this.triggerAppointmentStatus);
      }
      case 'radiology_finished': {
        // appointment status - radiology_finished 
        // trigger            - consultation_start_after_radiology
        if (role === ROLES.RADIOLOGY) {
          return this.renderStatusItems({ type: 'info', translateKey: 'radiology_finished' });
        }
        return this.renderStatusItems(
          { type: 'button', translateKey: 'start_consultation_button' },
          this.triggerAppointmentStatus);
      }
      case 'radiology_finished_during_consultation': {
        // appointment status -  radiology_finished_during_consultation
        // trigger            - consultation_start_after_radiology
        if (role === ROLES.RADIOLOGY) {
          return this.renderStatusItems({ type: 'info', translateKey: 'radiology_finished' });
        }
        return this.renderStatusItems(
          { type: 'button', translateKey: 'restart_consultation' }, this.triggerAppointmentStatus);
      }
      case 'consultation_restarted':
      case 'consultation_started_after_radiology': {
        // appointment status - consultation_restarted || consultation_started_after_radiology
        // trigger            - consultation_finish
        if (role === ROLES.RADIOLOGY) {
          return this.renderStatusItems({ type: 'info', translateKey: 'radiology_finished' });
        }
        return this.renderStatusItems({ type: 'button', translateKey: 'create_invoice' },
          redirectToInvoice);
      }
      case 'consultation_finished': {
        // appointment status - consultation_finished
        // trigger            - consultation_pay
        if (role === ROLES.RADIOLOGY) {
          return this.renderStatusItems({ type: 'info', translateKey: 'radiology_finished' });
        }
        if (appointment.invoice) {
          return this.renderStatusItems(
            { type: 'button', translateKey: 'confirm_pay_appointment_details' },
            e => {
              this.stopEvent(e);
              this.goToAppointmentDetails(e, TAB_INVOICE);
            }, (!appointment.invoice || localIsDisabledBtn));
        }
        return this.renderStatusItems({ type: 'button', translateKey: 'create_invoice' },
          redirectToInvoice);
      }

      case 'consultation_paid': // Paid
        return this.renderStatusItems({ type: 'info', translateKey: 'consultation_paid' });
      case 'consultation_canceled': // Canceled
        return this.renderStatusItems({ type: 'info', translateKey: 'consultation_canceled' });
      case 'consultation_nowshow': // No show
        return this.renderStatusItems({ type: 'info', translateKey: 'consultation_nowshow' });
      default: // available
        return this.renderStatusItems({ type: 'info', translateKey: 'available' });
    }
  }

  renderCertificate() {
    const { appointment: { invoice }, role } = this.props;
    if (invoice && role !== ROLES.RADIOLOGY) {
      return (
        <CertificateIcon
          invoice={invoice}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            this.goToAppointmentDetails(e, TAB_INVOICE);
          }}
        />
      );
    }
    return null;
  }



  render() {
    const { i18n, appointment } = this.props;
    const { updatedStatus } = this.state;
    const { patient, appointmentType, note } = appointment;

    return updatedStatus !== 'hide' ? (
      <TableRow
        onClick={this.goToAppointmentDetails}
        className={`${convertStatusToRowClass(appointment)} ${updatedStatus}`}
      >
        {/* ──── Appointment time ────────────────────────────────────────────────────────────── */}
        <td className="time">
          <div>
            <TimeBlock appointment={appointment} />
          </div>
        </td>
        {/* ──── Patient ─────────────────────────────────────────────────────────────────────── */}
        <td className="patient">
          <PatientBlock patient={patient} />
        </td>
        {/* ──── Appointment type ────────────────────────────────────────────────────────────── */}
        <td className="ap-type">
          <CellText>{i18n.t(appointmentType || 'unscheduled')}</CellText>
        </td>

        {/* ──── Note ────────────────────────────────────────────────────────────────────────── */}
        <td className="note">
          <NoteBlock note={note} />
        </td>

        {/* ──── Certificate ─────────────────────────────────────────────────────────────────── */}
        <td className="certificate">{this.renderCertificate()}</td>

        {/* ──── Triggers ────────────────────────────────────────────────────────────────────── */}
        <td id="triggers" className="triggers" style={{ cursor: 'default' }}>
          {this.viewStatus()}
        </td>
      </TableRow>
    ) : null;
  }
}

export default translate()(TableRowComponent);
